'use client'

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Truck, ExternalLink, Clock } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SubscriptionDetail } from '@/lib/api/subscriptions'
import { formatMoney } from '@/lib/money'
import { useOrgNavigate } from '@/hooks/use-app-navigate'

interface SubscriptionShippingCardProps {
	subscription: SubscriptionDetail
}

const ShippingAmount = ({
	amount,
	currency,
	isFree,
	t,
}: {
	amount: Record<string, { amount: number }> | undefined
	currency: string
	isFree: boolean
	t: (key: string) => string
}) => {
	if (isFree) {
		return <Badge variant="secondary">{t('shippingInfo.free')}</Badge>
	}
	const currencyAmount = amount?.[currency]?.amount
	if (currencyAmount == null) return null
	return <span className="font-medium">{formatMoney(currencyAmount, currency)}</span>
}

export const SubscriptionShippingCard = ({ subscription }: SubscriptionShippingCardProps) => {
	const { t } = useTranslation('subscriptions')
	const { orgRoutes } = useOrgNavigate()
	const { shippingRate, storeId, currency } = subscription

	if (!shippingRate) return null

	const shippingDetailUrl = storeId
		? `${orgRoutes.STORE(storeId)}/general/shippings/${shippingRate.id}`
		: undefined

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-base">
						<Truck className="h-4 w-4" />
						{t('shippingInfo.title')}
					</CardTitle>
					{shippingDetailUrl && (
						<Button variant="ghost" size="sm" asChild>
							<Link to={shippingDetailUrl}>
								<ExternalLink className="mr-1 h-3 w-3" />
								{t('shippingInfo.viewShippingRate')}
							</Link>
						</Button>
					)}
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<div className="flex items-center justify-between">
					<span className="text-muted-foreground text-sm">{t('shippingInfo.name')}</span>
					<span className="text-sm font-medium">{shippingRate.shippingRateName}</span>
				</div>
				{shippingRate.description && (
					<div className="flex items-center justify-between">
						<span className="text-muted-foreground text-sm">{t('shippingInfo.description')}</span>
						<span className="text-sm">{shippingRate.description}</span>
					</div>
				)}
				<div className="flex items-center justify-between">
					<span className="text-muted-foreground text-sm">{t('shippingInfo.amount')}</span>
					<ShippingAmount
						amount={shippingRate.amount}
						currency={currency}
						isFree={shippingRate.isFree}
						t={t}
					/>
				</div>
				{shippingRate.estimatedDeliveryTime != null && (
					<div className="flex items-center justify-between">
						<span className="text-muted-foreground text-sm">{t('shippingInfo.estimatedDelivery')}</span>
						<span className="flex items-center gap-1 text-sm">
							<Clock className="h-3 w-3" />
							{t('shippingInfo.estimatedDeliveryDays', { days: shippingRate.estimatedDeliveryTime })}
						</span>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
